import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { StartFreeButton } from '@/components/marketing/free-plan-cta'
import { Button } from '@/components/ui/button'

const SLUG = 'http-451-unavailable-for-legal-reasons'
const URL = `https://uptrack.app/blog/${SLUG}`
const HEADLINE = 'HTTP 451 Unavailable For Legal Reasons: what the error means and what to do'
const DESCRIPTION =
  'HTTP 451 Unavailable For Legal Reasons (RFC 7725): what the error means, how it differs from 403 and 404, and how to check it from other places.'
const PUBLISHED = '2026-10-12'

const FAQ: { q: string; a: string }[] = [
  {
    q: 'What does the status code 451 Unavailable For Legal Reasons mean?',
    a: 'It means a server, or a system between you and the server, is withholding the page because of a legal demand. RFC 7725 defines it. It is different from 403 Forbidden, which gives no reason, and from 404 Not Found, which says the page is missing.',
  },
  {
    q: 'Is error HTTP 451 a problem with my browser or my computer?',
    a: 'No. The server or a network in the path answered on purpose. Clearing your cache or restarting your browser does not change it, unless a cached 451 is stored. A 451 is cacheable by default, so a stored copy can outlive the block.',
  },
  {
    q: 'Does 451 mean the page exists?',
    a: 'Not necessarily. RFC 7725 says the 451 status implies neither the existence nor the non-existence of the resource named in the request.',
  },
  {
    q: 'Why does the same site work in one country and return 451 in another?',
    a: 'The block depends on where the request comes from. Some sites outside the European Economic Area answer EEA visitors with 451 instead of complying with GDPR. A legal order can also apply to one country only.',
  },
  {
    q: 'Can a site hide the reason for a block?',
    a: 'Yes. RFC 7725 notes that some legal authorities may want to avoid transparency, so a block can appear as 403, 404 or a timeout instead of 451. Clients cannot rely on a site using 451.',
  },
]

export const Route = createFileRoute('/_site/blog/http-451-unavailable-for-legal-reasons')({
  component: Http451Page,
  head: () => ({
    meta: [
      { title: 'HTTP 451 Unavailable For Legal Reasons: Meaning — Uptrack' },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: 'HTTP 451 Unavailable For Legal Reasons: Meaning — Uptrack' },
      { property: 'og:description', content: DESCRIPTION },
      { property: 'og:image', content: `https://uptrack.app/og/blog-${SLUG}.png` },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: URL },
      { property: 'article:published_time', content: PUBLISHED },
    ],
    links: [{ rel: 'canonical', href: URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: HEADLINE,
          description: DESCRIPTION,
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app' },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }),
})

const compare = [
  {
    code: '403 Forbidden',
    says: 'The server understood the request and refuses it. It gives no reason.',
    cache: 'Not cacheable by default',
  },
  {
    code: '404 Not Found',
    says: 'The server has no page at this address, or does not want to say if it has one.',
    cache: 'Cacheable by default',
  },
  {
    code: '410 Gone',
    says: 'The page was here and was removed on purpose. It will not return.',
    cache: 'Cacheable by default',
  },
  {
    code: '451 Unavailable For Legal Reasons',
    says: 'The page is withheld because of a legal demand. It does not say if the page exists.',
    cache: 'Cacheable by default',
  },
]

function Http451Page() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 12, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">{HEADLINE}</h1>

      <section className="mb-12">
        <p className="mb-4 text-lg leading-[1.7] text-body">
          You open a page and you get "451 Unavailable For Legal Reasons". This is not a broken server and it is not a
          typo in the address. A server, or a network between you and the server, has decided on purpose not to show you
          this page, because of a legal demand.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          This guide explains what the status code 451 means, who sends it, how it differs from 403 and 404, and what to
          do if you are a visitor, a site owner or someone who monitors websites.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">What does HTTP 451 Unavailable For Legal Reasons mean?</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          HTTP 451 is a status code from the 4xx family. It is defined in RFC 7725, "An HTTP Status Code to Report Legal
          Obstacles", written by Tim Bray and published in February 2016. The RFC says the code is for the case where a
          server operator has received a legal demand to deny access to a resource, or to a set of resources that
          includes the requested one.
        </p>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          The number is a reference to Ray Bradbury's novel <em>Fahrenheit 451</em>, in which books are outlawed. The RFC
          itself only thanks Ray Bradbury in its acknowledgements and does not explain the choice of number.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          Two details matter. First, a 451 says nothing about the page itself: the RFC states that the code implies
          neither that the resource exists nor that it does not. Second, if the legal demand ends, a request may still
          fail. The block is a statement about the law, not about the server's health.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Who sends a 451, and what does it look like?</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          Often the site operator sends it. But the RFC says the blocking server might not be the origin server. It names
          ISPs, cache providers and DNS servers as entities in the path that could deny access, and says that legal
          demands typically most directly affect ISPs and search engines.
        </p>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          The RFC asks the sender to explain the block in the response body: who made the demand, which law applies and
          which people and resources it covers. It also asks the blocking entity to identify itself in a{' '}
          <code>Link</code> header with the relation <code>blocked-by</code>. This is the example from the RFC:
        </p>
        <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-card p-5 text-sm leading-relaxed">
          <code>{`HTTP/1.1 451 Unavailable For Legal Reasons
Link: <https://spqr.example.org/legislatione>; rel="blocked-by"
Content-Type: text/html`}</code>
        </pre>
        <p className="text-lg leading-[1.7] text-body">
          The <code>blocked-by</code> link names the party that actually implements the block, not the party that
          demanded it. Both the explanation and the link are only recommendations ("SHOULD") in the RFC, so many 451 responses carry
          neither. To see the headers of a
          page yourself, run <code>curl -i</code> on the address.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">451 compared with 403, 404 and 410</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          All four codes mean "you do not get the page", but they tell you different things.
        </p>
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Code</th>
                <th className="pb-3 px-3 text-left font-medium text-muted-foreground">What it says</th>
                <th className="pb-3 pl-3 text-left font-medium text-muted-foreground">Caching</th>
              </tr>
            </thead>
            <tbody>
              {compare.map((row) => (
                <tr key={row.code} className="border-b align-top last:border-0">
                  <td className="py-3 pr-4 font-medium">{row.code}</td>
                  <td className="py-3 px-3 text-body">{row.says}</td>
                  <td className="py-3 pl-3 text-body">{row.cache}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-lg leading-[1.7] text-body">
          The caching column matters. A 451 response is cacheable by default, unless the request method or explicit cache
          controls say otherwise. A 403 is not on the list of codes that are cacheable by default. So a cache can keep
          serving a 451 after the block is gone.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Why the same site returns 451 in one country and 200 in another</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          The block depends on where the request comes from. The best-known case followed the start of GDPR. Some
          websites outside the European Economic Area chose to answer visitors from the EEA with 451 instead of
          complying with the new privacy law. Many regional news sites in the United States did this. The GDELT Project
          crawls the web and reported that these errors appeared only from its crawlers located in EU countries, not
          from crawlers elsewhere. One affected site showed a notice that it was not intended for people in the EEA.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          Sources disagree on how many sites do this, so treat it as one common cause, not the only one. A court order,
          a copyright takedown or a sanctions rule can also apply to one country and not to others.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">You may never see a 451</h2>
        <p className="text-lg leading-[1.7] text-body">
          RFC 7725 says plainly that clients cannot rely on the use of the 451 code. Some authorities want to avoid
          transparency and do not want the demand disclosed at all. So a legal block can also appear as a 403, a 404, an
          empty answer, a DNS failure or a timeout. When a page fails only in one country and you see no reason, a legal
          block is one of the possible causes.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">What to do about error HTTP 451</h2>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="font-sans mb-2 font-semibold">If you are a visitor</h3>
            <p className="text-lg leading-[1.7] text-body">
              Read the page. It often says who blocked it and why. You cannot fix a 451 from your side: the decision is
              the operator's or the law's. Contact the site if you think the block is a mistake. Check the law and the
              site's terms before you try to change your apparent location.
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="font-sans mb-2 font-semibold">If your own site returns a 451</h3>
            <p className="text-lg leading-[1.7] text-body">
              Find out if you chose it. Look at the geo rules of your CDN, your firewall and your hosting platform.
              Some platforms block regions for you. If the block is intended, send the explanation and the{' '}
              <code>blocked-by</code> link as the RFC asks. If it is not, remove the rule and purge your caches,
              because a 451 can stay cached.
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="font-sans mb-2 font-semibold">If you monitor the site</h3>
            <p className="text-lg leading-[1.7] text-body">
              A 451 means the server is reachable and answering on purpose. Decide what that means for you. If you block
              a country on purpose, a 451 from that country is expected and should not wake you up. If you did not
              block anyone, a 451 means a rule or a provider changed without you knowing, and you want an alert. In
              Uptrack you can set the expected status codes of an HTTP monitor for the first case.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Check a 451 from other locations</h2>
        <p className="mb-4 text-lg leading-[1.7] text-body">
          Because a block depends on location, one check is not enough. Use the free{' '}
          <Link to="/tools/is-website-down" className="font-medium underline">
            website down checker
          </Link>{' '}
          to request the page from 24 locations on six continents. The checker treats any answer below 500 as a reply
          from a working server and shows the status code for each city. A page that is blocked in one region shows
          451 there and 200 in the others. That pattern tells you the block is regional, not a server failure.
        </p>
        <p className="text-lg leading-[1.7] text-body">
          For the full method, read{' '}
          <Link to="/blog/how-to-check-if-a-website-is-down-from-multiple-locations" className="font-medium underline">
            how to check if a website is down from multiple locations
          </Link>
          .
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">When this is not the right tool</h2>
        <ul className="list-disc space-y-3 pl-6 text-lg leading-[1.7] text-body">
          <li>
            <strong>You need the reason.</strong> The checker shows the status code and the response time. It does not
            show the legal reason. For that, read the response body and the <code>Link</code> header with{' '}
            <code>curl -i</code>.
          </li>
          <li>
            <strong>You need legal advice.</strong> A status code tells you what a server did. It does not tell you what
            the law requires of you. Ask a lawyer.
          </li>
          <li>
            <strong>You want to bypass a block.</strong> This guide does not help with that, and a monitoring tool will
            not either.
          </li>
          <li>
            <strong>The block is from a private network.</strong> A company or school can filter traffic inside its own
            network. The checker tests from public data centers and will not see it.
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">FAQ</h2>
        <div className="space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="font-sans mb-1 font-semibold">{f.q}</h3>
              <p className="text-lg leading-[1.7] text-body">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Know when a status code changes</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          Monitor your site every 30 seconds and get an alert when it fails. Free for 5 monitors. Paid plans from $3/mo
          with a 30-day money-back guarantee.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <StartFreeButton />
          <Button size="lg" variant="outline" asChild>
            <Link to="/pricing">
              See plans
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </article>
  )
}
